import { useEffect, useState } from 'react';
import { Sparkles, BookOpen, Heart, Zap, Shield, Crown, Gem, Info, HandHeart, Users, Gift } from 'lucide-react';
import api from '../api';

const LEVELS = ['Newcomer', 'Apprentice', 'Contributor', 'Specialist', 'Pillar', 'Legend', 'Icon'];

const STATUS_CONFIG = {
  Newcomer:    { icon: Sparkles,  bg: 'bg-sky-100',      text: 'text-sky-700',      border: 'border-sky-300',      bar: 'from-sky-400 to-sky-500',       range: '1–50' },
  Apprentice:  { icon: BookOpen,  bg: 'bg-teal-100',     text: 'text-teal-700',     border: 'border-teal-300',     bar: 'from-teal-400 to-teal-500',     range: '51–150' },
  Contributor: { icon: Heart,     bg: 'bg-emerald-100',  text: 'text-emerald-700',  border: 'border-emerald-300',  bar: 'from-emerald-400 to-green-500', range: '151–350' },
  Specialist:  { icon: Zap,       bg: 'bg-amber-100',    text: 'text-amber-700',    border: 'border-amber-300',    bar: 'from-amber-400 to-yellow-500',  range: '351–700' },
  Pillar:      { icon: Shield,    bg: 'bg-orange-100',   text: 'text-orange-700',   border: 'border-orange-300',   bar: 'from-orange-400 to-orange-500', range: '701–1400' },
  Legend:      { icon: Crown,     bg: 'bg-violet-100',   text: 'text-violet-700',   border: 'border-violet-300',   bar: 'from-violet-400 to-purple-500', range: '1401–3000' },
  Icon:        { icon: Gem,       bg: 'bg-rose-100',     text: 'text-rose-700',     border: 'border-rose-300',     bar: 'from-rose-400 to-pink-500',     range: '3001+' },
};

const SOURCE_META = {
  loans:     { icon: HandHeart, tint: 'text-emerald-600', bg: 'bg-emerald-50' },
  invites:   { icon: Users,     tint: 'text-sky-600',     bg: 'bg-sky-50' },
  giveaways: { icon: Gift,      tint: 'text-rose-600',    bg: 'bg-rose-50' },
};

/** Points breakdown popover — lazy-loaded when the user hovers/taps the info
 *  trigger. Only mounted on `own` profile card since the endpoint requires auth
 *  and returns the current user's data. */
function PointsBreakdown({ open, data, loading }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-label="Points breakdown"
      data-testid="points-breakdown-popover"
      className="absolute z-30 top-full left-0 mt-2 w-72 bg-white rounded-xl border border-stone-200 shadow-xl p-4"
    >
      <div className="text-xs font-bold uppercase tracking-wide text-gray-500 font-nunito mb-2">
        How you earned {data?.total ?? 0} point{data?.total === 1 ? '' : 's'}
      </div>
      {loading || !data ? (
        <div className="space-y-2 py-2">
          {[1,2,3].map(i => <div key={i} className="h-8 bg-stone-100 rounded animate-pulse"/>)}
        </div>
      ) : (
        <div className="space-y-2">
          {['loans','invites','giveaways'].map(key => {
            const s = data.sources[key];
            const meta = SOURCE_META[key];
            const MetaIcon = meta.icon;
            return (
              <div key={key} data-testid={`breakdown-${key}`} className="flex items-center justify-between gap-2 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-lg ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                    <MetaIcon size={14} className={meta.tint}/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-nunito font-semibold text-gray-800 leading-tight">{s.label}</div>
                    <div className="text-[11px] text-gray-400 font-nunito leading-tight">
                      {s.count} × {s.per_event}pt · {s.hint}
                    </div>
                  </div>
                </div>
                <div className={`text-sm font-nunito font-bold ${meta.tint}`}>+{s.points}</div>
              </div>
            );
          })}
          {data.unattributed > 0 && (
            <div className="flex items-center justify-between gap-2 py-1.5 border-t border-stone-100 pt-2 mt-1">
              <div className="text-xs text-gray-500 font-nunito">Legacy / bonus</div>
              <div className="text-sm font-nunito font-bold text-gray-500">+{data.unattributed}</div>
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-gray-400 font-nunito mt-3 pt-2 border-t border-stone-100 leading-snug">
        Fastest way up: <span className="font-semibold text-rose-600">give an item</span> (+5) &gt; <span className="font-semibold text-sky-600">invite a friend</span> (+3) &gt; <span className="font-semibold text-emerald-600">lend</span> (+1).
      </p>
    </div>
  );
}

export default function StatusProgress({ statusProgress, compact = false, showBreakdown = false }) {
  const [popOpen, setPopOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showBreakdown || !popOpen || breakdown) return;
    setLoading(true);
    api.get('/users/me/points-breakdown')
      .then(r => setBreakdown(r.data))
      .catch(() => setBreakdown(null))
      .finally(() => setLoading(false));
  }, [popOpen, showBreakdown, breakdown]);

  useEffect(() => {
    if (!popOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest('[data-testid="points-breakdown-popover"]') &&
          !e.target.closest('[data-testid="points-breakdown-trigger"]')) {
        setPopOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [popOpen]);

  if (!statusProgress) return null;

  const { current_status, loan_count, next_status, loans_to_next, progress_in_level, is_max_level } = statusProgress;
  const config = STATUS_CONFIG[current_status] || STATUS_CONFIG.Newcomer;
  const Icon = config.icon;
  const levelNum = LEVELS.indexOf(current_status) + 1;

  if (compact) {
    return (
      <div className="flex flex-col items-start gap-1" data-testid="status-progress-compact">
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
          <Icon size={13} />
          {current_status}
        </div>
        {!is_max_level && (
          <span className="text-xs text-gray-400 font-nunito">
            {loans_to_next} pt{loans_to_next !== 1 ? 's' : ''} to {next_status}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-100 p-5 relative" data-testid="status-progress-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.bg}`}>
            <Icon size={24} className={config.text} />
          </div>
          <div>
            <h3 className="font-poppins font-semibold text-gray-900">{current_status}</h3>
            <div className="flex items-center gap-1.5">
              <p className="text-sm text-gray-500 font-nunito">{loan_count} total point{loan_count !== 1 ? 's' : ''} earned</p>
              {showBreakdown && (
                <button
                  type="button"
                  onClick={() => setPopOpen(v => !v)}
                  aria-label="Show points breakdown"
                  data-testid="points-breakdown-trigger"
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${popOpen ? 'bg-stone-200 text-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-stone-100'}`}
                >
                  <Info size={12}/>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}>
          Level {levelNum} / {LEVELS.length}
        </div>
      </div>

      <PointsBreakdown open={showBreakdown && popOpen} data={breakdown} loading={loading} />

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 font-nunito mb-1.5">
          <span>{current_status}</span>
          <span>{is_max_level ? 'Max Level!' : next_status}</span>
        </div>
        <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${is_max_level ? 'from-rose-400 to-pink-500' : config.bar}`}
            style={{ width: `${progress_in_level}%` }}
          />
        </div>
      </div>

      {/* Progress Text */}
      <div className="text-center">
        {is_max_level ? (
          <p className={`text-sm font-nunito font-semibold flex items-center justify-center gap-2 ${config.text}`}>
            <Gem size={15} />
            You've reached Icon status — the highest level!
          </p>
        ) : (
          <p className="text-sm font-nunito text-gray-600">
            <span className={`font-semibold ${config.text}`}>{loans_to_next}</span> more point{loans_to_next !== 1 ? 's' : ''} to reach{' '}
            <span className={`font-semibold ${config.text}`}>{next_status}</span>
          </p>
        )}
      </div>

      {/* All Levels Legend */}
      <div className="mt-4 pt-4 border-t border-stone-100">
        <div className="grid grid-cols-7 gap-1">
          {LEVELS.map((status) => {
            const sc = STATUS_CONFIG[status];
            const SIcon = sc.icon;
            const isActive = status === current_status;
            const isPast = LEVELS.indexOf(status) < LEVELS.indexOf(current_status);

            return (
              <div key={status} className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isActive
                    ? `${sc.bg} ring-2 ring-offset-1 ${sc.border.replace('border-', 'ring-')}`
                    : isPast
                      ? sc.bg
                      : 'bg-stone-100'
                }`}>
                  <SIcon size={14} className={isActive || isPast ? sc.text : 'text-stone-400'} />
                </div>
                <span className={`text-[9px] mt-1 font-nunito text-center leading-tight ${isActive ? 'font-bold text-gray-900' : 'text-gray-400'}`}>
                  {status}
                </span>
                <span className="text-[8px] text-gray-400 font-nunito text-center">{sc.range}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
